import React, { useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
} from 'chart.js';
import { Chart, Scatter } from 'react-chartjs-2';
import moment from 'moment';
import 'chartjs-adapter-moment';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    TimeScale,
    Title,
    Tooltip,
    Legend
);

/**
 * Process presence data to identify bed exits
 * @param {Array} presenceData - Array of presence data points {t: timestamp, y: state}
 * @returns {Array} Array of bed exit events {t: timestamp, y: 1}
 */
const detectBedExits = (presenceData) => {
    if (!presenceData || !presenceData.length) return [];
    
    const bedExits = [];
    
    // Start from the second data point to compare with previous
    for (let i = 1; i < presenceData.length; i++) {
        const prevPoint = presenceData[i-1];
        const currPoint = presenceData[i];
        
        // If state changes from 1 (present) to 0 (absent), we have a bed exit
        if (prevPoint.y === 1 && currPoint.y === 0) {
            bedExits.push({
                t: currPoint.t, // Use the timestamp when the person became absent
                y: 1           // Fixed value for visualization
            });
        }
    }
    
    console.log(`Detected ${bedExits.length} bed exits from presence data`);
    return bedExits;
};

const ActivityChart = ({ movementData, presenceData, bedExitsData, startTime, endTime, smoothGraphData }) => {
    // Use provided bedExitsData if available, otherwise detect from presence data
    const bedExitData = bedExitsData || detectBedExits(presenceData);

    // Only render the movement data if it exists
    const hasMovementData = movementData && movementData.length > 0;
    
    // Common chart options base
    const baseOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'index'
        },
        plugins: {
            legend: {
                display: true,
                position: 'top',
                onHover: (event, legendItem, legend) => {
                    const tooltips = {
                        'Movement': 'Patient movement activity detected by the sensor',
                        'Bed Exits': 'Times when patient has left the bed',
                        'Patient Movement Score': 'Overall movement score (0-10)'  
                    };
                    
                    // Change cursor to indicate interactivity
                    event.native.target.style.cursor = 'pointer';
                    
                    // Find tooltip container or create it if it doesn't exist
                    let tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.id = 'chart-legend-tooltip';
                        tooltipEl.style.position = 'absolute';
                        tooltipEl.style.background = 'rgba(0,0,0,0.7)';
                        tooltipEl.style.color = 'white';
                        tooltipEl.style.padding = '5px 10px';
                        tooltipEl.style.borderRadius = '3px';
                        tooltipEl.style.fontSize = '12px';
                        tooltipEl.style.pointerEvents = 'none';
                        tooltipEl.style.opacity = '0';
                        tooltipEl.style.transition = 'opacity 0.2s';
                        tooltipEl.style.zIndex = '10';
                        document.body.appendChild(tooltipEl);
                    }
                    
                    // Show tooltip with description
                    if (tooltips[legendItem.text]) {
                        tooltipEl.innerHTML = tooltips[legendItem.text];
                        tooltipEl.style.opacity = '1';
                        tooltipEl.style.left = event.native.pageX + 'px';
                        tooltipEl.style.top = (event.native.pageY - 28) + 'px'; // Position above the cursor
                    }
                },
                onLeave: (event, legendItem, legend) => {
                    // Reset cursor
                    if (event.native && event.native.target) {
                        event.native.target.style.cursor = 'default';
                    }
                    
                    // Hide tooltip
                    const tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (tooltipEl) {
                        tooltipEl.style.opacity = '0';
                    }
                }
            },
            datalabels: {
                display: false
            },
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'hour',
                    displayFormats: {
                        hour: 'MM/DD h:mma'
                    }
                },
                min: startTime,
                max: endTime,
                title: {
                    display: true,
                    text: 'Time'
                },
                ticks: {
                    maxRotation: 0,
                    autoSkip: true,
                    padding: 8,
                    align: 'center'
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    tickLength: 8,
                    offset: false
                },
                border: {
                    display: true
                },
                afterFit: (scale) => {
                    // Ensure consistent padding/margin across charts
                    scale.paddingRight = 15;
                    scale.paddingLeft = 15;
                }
            }
        },
        layout: {
            padding: {
                left: 10,
                right: 20,
                top: 0,
                bottom: 5
            }
        }
    };

    // Movement chart specific options
    const movementOptions = {
        ...baseOptions,
        plugins: {
            ...baseOptions.plugins,
            title: {
                display: true,
                text: 'Movement Activity',
                font: { size: 16 }
            },
            tooltip: {
                ...baseOptions.plugins.tooltip,
                callbacks: {
                    label: function(context) {
                        const value = context.parsed.y;
                        const time = moment(context.parsed.x).format('MM/DD h:mma');
                        return `Movement: ${value.toFixed(1)} at ${time}`;
                    }
                }
            }
        },
        scales: {
            ...baseOptions.scales,
            y: {
                type: 'linear',
                min: 0,
                max: 100,
                stepSize: 10,
                title: {
                    display: true,
                    text: 'Movement',
                    font: {
                        family: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        size: "14",
                        weight: "bold"
                    }
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            }
        }
    };

    // Bed Exit chart specific options
    const bedExitOptions = {
        ...baseOptions,
        plugins: {
            ...baseOptions.plugins,
            title: {
                display: true,
                text: 'Bed Exits',
                font: { size: 16 }
            },
            tooltip: {
                ...baseOptions.plugins.tooltip,
                callbacks: {
                    label: function(context) {
                        const time = moment(context.parsed.x).format('MM/DD h:mma');
                        return `Bed Exit at ${time}`;
                    }
                }
            }
        },
        scales: {
            ...baseOptions.scales,
            y: {
                type: 'linear',
                min: 0.5,
                max: 1.5,
                ticks: {
                    stepSize: 1,
                    callback: function(value) {
                        return value === 1 ? 'Exit' : '';
                    }
                },
                title: {
                    display: true,
                    text: 'Bed Exits',
                    font: {
                        family: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        size: "14",
                        weight: "bold"
                    }
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            }
        }
    };

    // Print detailed data in the console for activity chart
    console.log('ActivityChart Data:', {
        timeRange: {
            startTime: new Date(startTime).toISOString(),
            endTime: new Date(endTime).toISOString(),
            durationHours: moment(endTime).diff(moment(startTime), 'hours', true).toFixed(2)
        },
        movementData: {
            totalPoints: movementData?.length || 0,
            timeSpan: movementData?.length ? {
                firstPoint: new Date(movementData[0]?.t).toISOString(),
                lastPoint: new Date(movementData[movementData.length - 1]?.t).toISOString()
            } : null,
            valueRange: movementData?.length ? {
                min: Math.min(...movementData.map(d => d.y)),
                max: Math.max(...movementData.map(d => d.y)),
                avg: (movementData.reduce((sum, d) => sum + d.y, 0) / movementData.length).toFixed(2)
            } : null,
            samplePoints: movementData?.slice(0, 5),
        },
        presenceData: {
            totalPoints: presenceData?.length || 0,
            presentPoints: presenceData?.filter(p => p.y === 1).length || 0,
            absentPoints: presenceData?.filter(p => p.y === 0).length || 0,
            samplePoints: presenceData?.slice(0, 5)
        },
        bedExitData: {
            totalExits: bedExitData?.length || 0,
            exitTimes: bedExitData?.map(exit => moment(exit.t).format('HH:mm:ss')) || [],
            samplePoints: bedExitData?.slice(0, 5)
        },
        renderingOptions: {
            smoothGraphData: !!smoothGraphData
        }
    });

    // Movement chart data
    const movementChartData = {
        datasets: [
            {
                type: 'scatter',
                label: 'Movement',
                data: movementData || [],
                borderColor: "#56B6A5",
                backgroundColor: "#56B6A5",
                pointRadius: 3,
                pointHoverRadius: 5,
                showLine: false,
                pointBorderColor: 'rgba(86, 182, 165, 0.8)',
                pointBackgroundColor: 'rgba(86, 182, 165, 0.8)',
                pointHoverBackgroundColor: '#56B6A5',
                pointHoverBorderColor: '#56B6A5',
                parsing: {
                    xAxisKey: 't',
                    yAxisKey: 'y'
                }
            }
        ]
    };

    // Bed Exit chart data
    const bedExitChartData = {
        datasets: [
            {
                type: 'scatter',
                label: 'Bed Exits',
                data: bedExitData || [],
                borderColor: "#645403",
                backgroundColor: "#645403",
                pointRadius: 5,
                pointHoverRadius: 7,
                showLine: false,
                pointStyle: 'triangle',
                pointBorderColor: 'rgba(100, 84, 3, 0.8)',
                pointBackgroundColor: 'rgba(100, 84, 3, 0.8)',
                pointHoverBackgroundColor: '#645403',
                pointHoverBorderColor: '#645403',
                parsing: {
                    xAxisKey: 't',
                    yAxisKey: 'y'
                }
            }
        ]
    };

    return (
        <div>
            {/* {hasMovementData && (
                <div style={{ height: '180px', marginBottom: '20px', width: '100%' }}>
                    <Scatter options={movementOptions} data={movementChartData} />
                </div>
            )} */}
            <div style={{ height: '180px', marginBottom: '20px', width: '100%' }}>
                <Scatter options={bedExitOptions} data={bedExitChartData} />
            </div>
        </div>
    );
};

export default ActivityChart;
